import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { MultiTrackExportFormValues } from 'app/components/Releases/ExportSoundCloudReleaseDialog/types';
import { extendedDefaultValues } from 'app/components/Releases/ExportSoundCloudReleaseDialog/constants';
import { dynamicYupResolver } from 'app/utils/utils';
import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Stack,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { InputField } from 'app/components/Core/ChakraInputField/InputField';
import { DateField, MessageBox } from 'app/components/Core';
import { ChakraAutocompleteField } from 'app/components/Core/ChakraAutocompleteField/ChakraAutocompleteField';
import { SwitchField } from 'app/components/Core/ChakraSwitchField';
import { ChakraTextareaField } from 'app/components/Core/ChakraTextareaField/ChakraTextareaField';
import { MultiTrackReleaseFormProps } from './types';

export const MultiTrackReleaseForm: React.FC<MultiTrackReleaseFormProps> = ({
  releaseTitle,
  playlistTypes,
  yupValidationSchema,
  onClose: onCloseProp,
  onSubmit,
  genres,
  isUserConnected,
  disabled,
}) => {
  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });
  const types = useMemo(
    () =>
      playlistTypes.map((type) => ({
        title: type.name,
        value: type.key,
      })),
    [playlistTypes]
  );

  const {
    control,
    handleSubmit,
    register,
    formState: { isValid, isDirty, errors },
  } = useForm<MultiTrackExportFormValues>({
    defaultValues: {
      ...extendedDefaultValues,
      playlist: {
        ...extendedDefaultValues.playlist,
        title: releaseTitle,
        type: types.find((type) => type.value === 'playlist') || types[0],
      },
    },
    mode: 'all',
    context: {
      yupValidationSchema,
    },
    resolver: dynamicYupResolver,
  });

  return (
    <Modal
      size="md"
      isOpen={isOpen}
      onClose={onClose}
      onCloseComplete={onCloseProp}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent data-testid="dialog" sx={{ minH: 280 }}>
        <ModalHeader data-testid="dialog-title" title="Export Release">
          Export Release
        </ModalHeader>
        <ModalBody data-testid="dialog-content" py={0}>
          <VStack spacing={0} as="form">
            <InputField
              label="Title"
              type="text"
              isDisabled
              overflow="hidden"
              textOverflow="ellipsis"
              data-testid="title"
              {...register('playlist.title')}
            />
            <SimpleGrid width="full" columns={2} columnGap={4}>
              <ChakraAutocompleteField
                control={control}
                name="playlist.type"
                label="Playlist type"
                options={types}
                dataTestId="playlist-type"
                menuPosition="fixed"
              />
              <ChakraAutocompleteField
                control={control}
                name="playlist.genre"
                label="Genre"
                options={genres}
                dataTestId="genres-autocomplete"
                isClearable
                menuPosition="fixed"
              />
              <Box h="70px">
                <SwitchField
                  label="Private"
                  labelProps={{
                    htmlFor: 'isPrivate',
                  }}
                  data-testid="private-label"
                  {...register('isPrivate')}
                />
              </Box>
              <Box h="70px">
                <SwitchField
                  label="Downloadable tracks"
                  labelProps={{
                    htmlFor: 'isDownloadable',
                  }}
                  data-testid="downloadable-label"
                  {...register('isDownloadable')}
                />
              </Box>
              <DateField<MultiTrackExportFormValues>
                control={control}
                name="releaseExportDate"
                label="Release Date"
                dataTestId="release-export-date"
                shouldSaveInvalidDate
              />
              <SwitchField
                label="Enable app playback"
                labelProps={{
                  htmlFor: 'isStreamable',
                }}
                tooltipProps={{
                  label: 'Make playable outside of SoundCloud and its apps.',
                  'data-testid': 'streamable-tooltip',
                }}
                data-testid="streamable-label"
                {...register('isStreamable')}
              />
              <ChakraTextareaField
                label="Playlist description"
                isInvalid={Boolean(errors?.playlist?.description)}
                errorMessage={errors?.playlist?.description?.message}
                width="100%"
                resize="none"
                wordBreak="break-word"
                formControlStyleProps={{
                  gridColumn: '1 / span 2',
                }}
                data-testid="playlist-description"
                {...register('playlist.description')}
              />
            </SimpleGrid>
            {!isUserConnected && (
              <MessageBox
                boxProps={{
                  mb: 4,
                  w: 'full',
                  'data-testid': 'info-message',
                }}
              >
                You need to connect to SoundCloud to export
              </MessageBox>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Stack spacing={4} direction="row">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit(onSubmit)}
              isDisabled={(isDirty && !isValid) || disabled}
            >
              {isUserConnected ? 'Export' : 'Connect & Export'}
            </Button>
          </Stack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
